import { API_MODE, delay } from "./helpers";
import { request } from "./http";
import { paths } from "./wire";
import * as mock from "@/lib/mocks/summary";

/**
 * Vision 2026-10-08 (2.976): the organisation and project SUMMARIES the
 * overview and the dashboard are drawn from, as the product lane serves them
 * (`GET /v1/orgs/{id}/summary`, `GET /v1/projects/{id}/summary`).
 *
 * They read the platform's PRE-AGGREGATES (SDD §11.3), never a live count over
 * findings, and they are counted over real rows: every tile drawn from them
 * links to a list that holds the same rows (the coordinator's rule, 2026-10-08).
 *
 * The product lane's definitions, carried here because the tiles rely on them:
 * - OPEN is a state in {open, triaged, in_fix, validating, reopened}; verified
 *   and closed are resolved.
 * - An issue's severity is the WORST severity among its findings.
 * - Every severity, state and validation key is ALWAYS present, 0 included.
 * - An open issue with no finding counts in the total and in no severity, so
 *   open issues' severities may sum to less than their total (never more).
 *
 * The answer is untrusted (invariant 9): parsed into typed figures and REFUSED
 * whole when a key is missing, a count is not a count, or a total is not the sum
 * of its breakdown. A missing key would otherwise read as 0, which is the one
 * thing the contract promises it never means.
 */

export const SUMMARY_SEVERITIES = ["critical", "high", "medium", "low", "info"] as const;
export type SummarySeverity = (typeof SUMMARY_SEVERITIES)[number];
export const OPEN_STATES = ["open", "triaged", "in_fix", "validating", "reopened"] as const;
export type OpenState = (typeof OPEN_STATES)[number];
export const VALIDATIONS = ["verified", "unverified"] as const;
export type Validation = (typeof VALIDATIONS)[number];

export interface OpenIssues {
  total: number;
  bySeverity: Record<SummarySeverity, number>;
  byState: Record<OpenState, number>;
}

export interface FindingCounts {
  total: number;
  bySeverity: Record<SummarySeverity, number>;
  /**
   * S-1037: null while the platform is still back-filling this split after an
   * upgrade (one watermark per tenant, so the organisation's and every
   * project's are null together). Shown as "not yet available", never as 0
   * or as a partial sum.
   */
  byValidation: Record<Validation, number> | null;
}

export interface ProjectRollup {
  projectId: string;
  name: string;
  openIssues: { total: number; bySeverity: Record<SummarySeverity, number> };
  /** null when no run of the project has concluded. */
  lastRunConcludedAt: string | null;
}

export interface LastRun {
  scanRunId: string;
  state: string;
  concludedAt: string | null;
  /** The profile's ID; the console names it from the served menu (2.962). Null when not recorded (S-1037's contract). */
  scanProfile: string | null;
}

interface SummaryCore {
  /** S-1037: the latest observation of the figures, not the request's time; null before the first. */
  asOf: string | null;
  openIssues: OpenIssues;
  findings: FindingCounts;
  targetsTotal: number;
  /** Queued plus running. */
  runsActive: number;
}

export interface OrganisationSummary extends SummaryCore {
  /** The top five projects by open issues, ties by name. */
  projects: ProjectRollup[];
  /**
   * 3.131: EVERY permitted project, uncapped, the same rows in the same order
   * (`all_projects`, the product lane at demo-vision-product 1584fb0b, for the
   * workspace cards). NOT REQUIRED: an older server lacks it, and then only the
   * top five are counted. Bounded by the permitted set, which the quota bounds.
   */
  allProjects?: ProjectRollup[];
}

export interface ProjectSummary extends SummaryCore {
  lastRun: LastRun | null;
}

/** S-1037: when the figures were last observed, as a sentence; it never claims a time it was not given. */
export function summaryFreshness(asOf: string | null): string {
  return asOf
    ? `Updated ${new Date(asOf).toISOString().slice(11, 16)} UTC.`
    : "Not counted yet: the platform has not observed these figures.";
}

export class SummaryShapeError extends Error {
  constructor(what: string) {
    super(`The platform's summary could not be read: ${what}.`);
    this.name = "SummaryShapeError";
  }
}

const isObject = (v: unknown): v is Record<string, unknown> => typeof v === "object" && v !== null && !Array.isArray(v);
const isCount = (v: unknown): v is number => typeof v === "number" && Number.isInteger(v) && v >= 0;
const isInstant = (v: unknown): v is string => typeof v === "string" && !Number.isNaN(Date.parse(v));

function counts<K extends string>(v: unknown, keys: readonly K[], what: string): Record<K, number> {
  if (!isObject(v)) throw new SummaryShapeError(`${what} is not a breakdown`);
  const out = {} as Record<K, number>;
  for (const k of keys) {
    if (!isCount(v[k])) throw new SummaryShapeError(`${what} has no count for ${k}`);
    out[k] = v[k] as number;
  }
  const extra = Object.keys(v).filter((k) => !(keys as readonly string[]).includes(k));
  if (extra.length) throw new SummaryShapeError(`${what} carries keys this console does not know (${extra.join(", ")})`);
  return out;
}

const sum = (r: Record<string, number>) => Object.values(r).reduce((a, n) => a + n, 0);

// Each part is its own count record (states, severities, validation results), so
// the parts are any count records, not one key set (aa6f1d0b).
function totalled(total: unknown, parts: [Record<string, number>, string][], what: string, atLeast: [Record<string, number>, string][] = []): number {
  if (!isCount(total)) throw new SummaryShapeError(`${what} has no total`);
  for (const [p, name] of parts) {
    if (sum(p) !== total) throw new SummaryShapeError(`${what}'s total ${total} is not the sum of its ${name} (${sum(p)})`);
  }
  // An open issue with NO finding has no worst severity: it counts in the total
  // and in no severity bucket (the product lane's definition, 2026-10-08), so
  // the severities may sum to LESS than the total, never more.
  for (const [p, name] of atLeast) {
    if (sum(p) > total) throw new SummaryShapeError(`${what}'s ${name} (${sum(p)}) exceed its total ${total}`);
  }
  return total;
}

function core(w: Record<string, unknown>): SummaryCore {
  // S-1037: null before the platform's first observation; anything else must be a time.
  if (w.as_of !== null && !isInstant(w.as_of)) throw new SummaryShapeError("as_of is neither a time nor null");
  const oi = w.open_issues;
  if (!isObject(oi)) throw new SummaryShapeError("no open_issues");
  const oiSev = counts(oi.by_severity, SUMMARY_SEVERITIES, "open issues by severity");
  const oiState = counts(oi.by_state, OPEN_STATES, "open issues by state");
  const f = w.findings;
  if (!isObject(f)) throw new SummaryShapeError("no findings");
  const fSev = counts(f.by_severity, SUMMARY_SEVERITIES, "findings by severity");
  // S-1037: null while the split is back-filled; LEFT OUT is still refused.
  const fVal = f.by_validation === null ? null : counts(f.by_validation, VALIDATIONS, "findings by validation");
  if (!isCount(w.targets_total)) throw new SummaryShapeError("no targets_total");
  if (!isCount(w.runs_active)) throw new SummaryShapeError("no runs_active");
  return {
    asOf: w.as_of as string | null,
    openIssues: { total: totalled(oi.total, [[oiState, "states"]], "open issues", [[oiSev, "severities"]]), bySeverity: oiSev, byState: oiState },
    findings: {
      // A null split is not summed: a partial sum would read as a smaller real number.
      total: totalled(f.total, fVal ? [[fSev, "severities"], [fVal, "validation results"]] : [[fSev, "severities"]], "findings"),
      bySeverity: fSev,
      byValidation: fVal,
    },
    targetsTotal: w.targets_total,
    runsActive: w.runs_active,
  };
}

export function organisationSummaryFromWire(w: unknown): OrganisationSummary {
  if (!isObject(w)) throw new SummaryShapeError("not an object");
  const base = core(w);
  if (!Array.isArray(w.projects)) throw new SummaryShapeError("no projects");
  if (w.projects.length > 5) throw new SummaryShapeError(`${w.projects.length} projects, above the top five`);
  const projects = w.projects.map((p, i) => rollupFromWire(p, `project ${i + 1}`));
  if (w.all_projects === undefined || w.all_projects === null) return { ...base, projects };
  if (!Array.isArray(w.all_projects)) throw new SummaryShapeError("all_projects is not a list");
  const seen = new Set<string>();
  const allProjects = w.all_projects.map((p, i) => {
    const r = rollupFromWire(p, `all_projects row ${i + 1}`);
    if (seen.has(r.projectId)) throw new SummaryShapeError(`${r.projectId} is listed twice in all_projects`);
    seen.add(r.projectId);
    return r;
  });
  return { ...base, projects, allProjects };
}

/** One project row, as `projects` and `all_projects` both carry it. */
function rollupFromWire(p: unknown, where: string): ProjectRollup {
  if (!isObject(p) || typeof p.project_id !== "string" || typeof p.name !== "string") throw new SummaryShapeError(`${where} is not named`);
  const oi = p.open_issues;
  if (!isObject(oi)) throw new SummaryShapeError(`${where} has no open_issues`);
  const sev = counts(oi.by_severity, SUMMARY_SEVERITIES, `${where}'s open issues`);
  if (p.last_run_concluded_at !== null && !isInstant(p.last_run_concluded_at)) throw new SummaryShapeError(`${where}'s last run time is unreadable`);
  return {
    projectId: p.project_id,
    name: p.name,
    openIssues: { total: totalled(oi.total, [], `${where}'s open issues`, [[sev, "severities"]]), bySeverity: sev },
    lastRunConcludedAt: (p.last_run_concluded_at as string | null) ?? null,
  };
}

export function projectSummaryFromWire(w: unknown): ProjectSummary {
  if (!isObject(w)) throw new SummaryShapeError("not an object");
  const base = core(w);
  let lastRun: LastRun | null = null;
  if (w.last_run !== null && w.last_run !== undefined) {
    const r = w.last_run;
    if (!isObject(r) || typeof r.scan_run_id !== "string" || typeof r.state !== "string" || (typeof r.scan_profile !== "string" && r.scan_profile !== null)) {
      throw new SummaryShapeError("last_run is not a run");
    }
    if (r.concluded_at !== null && !isInstant(r.concluded_at)) throw new SummaryShapeError("last_run's concluded_at is unreadable");
    lastRun = { scanRunId: r.scan_run_id, state: r.state, concludedAt: (r.concluded_at as string | null) ?? null, scanProfile: (r.scan_profile as string | null) ?? null };
  } else if (w.last_run === undefined) {
    throw new SummaryShapeError("no last_run (null when there is none)");
  }
  return { ...base, lastRun };
}

export async function getOrganisationSummary(organisationId: string): Promise<OrganisationSummary> {
  if (API_MODE === "http") return organisationSummaryFromWire(await request<unknown>(paths.organisationSummary(organisationId)));
  await delay(120);
  return organisationSummaryFromWire(mock.organisationSummary());
}

export async function getProjectSummary(projectId: string): Promise<ProjectSummary> {
  if (API_MODE === "http") return projectSummaryFromWire(await request<unknown>(paths.projectSummary(projectId)));
  await delay(120);
  return projectSummaryFromWire(mock.projectSummary(projectId));
}
